import { randomUUID as uuidv4 } from 'expo-crypto';
import { usePathname } from 'expo-router';
import { useIntl } from 'react-intl';

import { toaster } from 'components/toaster/toaster';
import { useCart } from 'hooks/use-cart';
import { useMinimalCart } from 'hooks/use-minimal-cart';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LoyaltyAppliedOffer } from 'state/loyalty/types';
import { useUnifiedMenuContext } from 'state/unified-menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { mapImageResourceToSanityImage } from 'state/unified-menu/utils';
import {
  mapMenuSelectionsToMinimalCartEntry,
  mapSelectedProductToMenuSelections,
} from 'utils/minimal-cart';
import { useMemoAll } from 'utils/use-memo-all';

import { SlotsSelections } from './types';
import { useProductWizardFlow } from './use-product-wizard-flow';

export const useProductCart = () => {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const { indexedMenu } = useUnifiedMenuContext();
  const { addItemToMinimalCart, hydrateMinimalCart } = useMinimalCart();
  const { posServiceMode } = useMenuQueryOptions();
  const { cartIdEditing, rewardBenefitId, rewardId } = useProductWizardFlow();
  const { removeFromCart } = useCart();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);

  const { formatMessage } = useIntl();

  const addToCart = (
    selectedProduct: ILocalizedMenuEntry,
    quantity: number,
    slotsSelections: SlotsSelections
  ) => {
    let menuSelections = mapSelectedProductToMenuSelections(
      selectedProduct,
      quantity,
      slotsSelections
    );

    if (rewardId) {
      menuSelections = [
        {
          id: rewardId,
          quantity,
          menuSelections,
        },
      ];
    }

    const [mappedEntry] = mapMenuSelectionsToMinimalCartEntry(menuSelections, {
      menu: indexedMenu,
      posServiceMode,
      url: pathname,
    });

    const minimalCartEntry = {
      ...mappedEntry,
      cartId: cartIdEditing || uuidv4(),
      ...(cartIdEditing && { isEdit: true }),
      ...(!cartIdEditing && rewardBenefitId && { rewardBenefitId }),
    };

    if (cartIdEditing) {
      hydrateMinimalCart([minimalCartEntry]);
    } else {
      addItemToMinimalCart(minimalCartEntry);
    }

    if ('__parentLoyaltyEntityId' in selectedProduct && selectedLoyaltyOffer) {
      if (!selectedLoyaltyOffer.isStackable) {
        appliedOffers.forEach(offer => {
          if (!offer.isStackable && !!offer.cartId) {
            removeFromCart({ cartId: offer.cartId });
          }
        });
      }
      dispatch(
        actions.loyalty.applyOffer({
          id: selectedLoyaltyOffer.loyaltyEngineId,
          type: (selectedLoyaltyOffer as LoyaltyAppliedOffer).type,
          cartId: minimalCartEntry.cartId,
          isStackable: selectedLoyaltyOffer.isStackable,
          cmsId: selectedLoyaltyOffer._id,
        })
      );
      dispatch(actions.loyalty.setSelectedOffer(null));
    }

    return toaster.addToCartToast(
      `${formatMessage(
        { id: cartIdEditing ? 'updateCartSuccess' : 'addToCartSuccess' },
        { itemName: minimalCartEntry.name }
      )}`,
      rewardId
        ? mapImageResourceToSanityImage(
            selectedProduct.image?.resource ?? '',
            selectedProduct.image?.altText?.locale ?? ''
          )
        : minimalCartEntry.image
    );
  };

  return useMemoAll({
    addToCart,
  });
};
